import { Injectable } from '@angular/core';

export interface AppVersionInfo {
  release: string;
  buildVersion: string;
  builtAtUtc: string | null;
  gitHash: string | null;
  source: 'version.json' | 'fallback';
}

type VersionPayload = {
  version?: string;
  builtAtUtc?: string;
  release?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AppVersionService {
  private cachedInfo: AppVersionInfo | null = null;

  async getVersionInfo(forceRefresh = false): Promise<AppVersionInfo> {
    if (this.cachedInfo && !forceRefresh) {
      return this.cachedInfo;
    }

    const versionUrl = new URL('version.json', document.baseURI);
    versionUrl.searchParams.set('ngsw-bypass', 'true');
    versionUrl.searchParams.set('t', String(Date.now()));

    try {
      // Bounded timeout: this call must never hang the caller when the server is
      // completely unreachable (as opposed to actively refusing the connection).
      const abortController = new AbortController();
      const timeoutId = setTimeout(() => abortController.abort(), 10_000);
      let response: Response;
      try {
        response = await fetch(versionUrl.toString(), { cache: 'no-store', signal: abortController.signal });
      } finally {
        clearTimeout(timeoutId);
      }
      if (!response.ok) {
        throw new Error(`Failed to load version.json (${response.status})`);
      }

      const payload = (await response.json()) as VersionPayload;
      const buildVersion = this.normalizeString(payload.version) || 'unknown';
      const release = this.normalizeString(payload.release) || this.deriveReleaseFromBuild(buildVersion) || 'unknown';

      this.cachedInfo = {
        release,
        buildVersion,
        builtAtUtc: this.normalizeString(payload.builtAtUtc),
        gitHash: this.extractGitHash(buildVersion),
        source: 'version.json',
      };
      return this.cachedInfo;
    } catch {
      this.cachedInfo = {
        release: 'dev-local',
        buildVersion: 'dev-local',
        builtAtUtc: null,
        gitHash: null,
        source: 'fallback',
      };
      return this.cachedInfo;
    }
  }

  private normalizeString(value: unknown): string | null {
    return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
  }

  private extractGitHash(version: string): string | null {
    const hashMatch = /-([0-9a-f]{7,40})$/i.exec(version);
    return hashMatch?.[1] ?? null;
  }

  private deriveReleaseFromBuild(version: string): string | null {
    const firstToken = version.split('-')[0]?.trim();
    if (!firstToken) {
      return null;
    }
    return /^\d+$/.test(firstToken) ? firstToken : null;
  }
}
